import { HttpClient } from '@angular/common/http'
import { Injectable, OnDestroy } from '@angular/core'
import { Capacitor } from '@capacitor/core'
import { NavController } from '@ionic/angular'
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client'
import { AuthResult } from 'angular-auth-oidc-client/lib/flows/callback-context'
import { jwtDecode } from 'jwt-decode'
import {
  BehaviorSubject,
  firstValueFrom,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs'
import { AppRoute } from 'src/app/app.routes.fixtures'
import { ConstantsUtility } from 'src/app/utility/constants.utility'
import { environment } from 'src/environments/environment'
import { AnalyticsService } from '../analytics/analytics.service'
import {
  ChatUserData,
  UserOAuth2Model,
  UserProfile,
} from './auth.service.fixtures'

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()
  private accessToken = new BehaviorSubject<string>(undefined)
  private refreshToken = new BehaviorSubject<string>(undefined)
  private isChatLoginActive = false
  readonly isAuthenticated = new BehaviorSubject<boolean>(false)
  readonly chatUserData = new BehaviorSubject<ChatUserData>(undefined)

  private static readonly SHOW_DEBUG = false

  userProfile = new BehaviorSubject<UserProfile>(undefined)

  get isAdmin(): boolean {
    return this.userProfile.value?.isAdmin ?? false
  }

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private analyticsService: AnalyticsService,
    private httpClient: HttpClient,
    private navigationController: NavController
  ) {
    this.accessToken
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((token) => {
        this.checkUserRoles(token)
        if (!this.chatUserData.value) {
          this.refreshChatToken()
        }
      })
    this.userProfile
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((userProfile) =>
        this.analyticsService.setUserId(userProfile?.uuid)
      )
  }

  ngOnDestroy() {
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  initAuthConfiguration(): void {
    if (AuthService.SHOW_DEBUG) {
      console.log(`[checkAuth] init authConfiguration`)
    }

    this.oidcSecurityService
      .checkAuth()
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(({ isAuthenticated, userData, accessToken, errorMessage }) => {
        if (environment.oauthConfig.showDebugInformation || true) {
          if (AuthService.SHOW_DEBUG) {
            console.log(`[checkAuth] is authenticated: ${isAuthenticated}`)
            console.log(`[checkAuth] userData: ${userData}`)
            if (errorMessage) {
              console.log(`[checkAuth] error: ${errorMessage}`)
            }
          }
        }

        this.isAuthenticated.next(isAuthenticated)
        this.accessToken.next(accessToken)
        if (!this.chatUserData.value) {
          this.refreshChatToken()
        }
      })

    this.oidcSecurityService.isAuthenticated$
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((authenticationResult) =>
        this.isAuthenticated.next(authenticationResult.isAuthenticated)
      )

    this.oidcSecurityService.userData$
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((userDataResult) =>
        this.userProfile.next(this.getUserFromObject(userDataResult.userData))
      )
  }

  login() {
    if (AuthService.SHOW_DEBUG) {
      console.log('[AuthService] Logging in')
    }

    if (Capacitor.isNativePlatform()) {
      const loginSubscription = this.oidcSecurityService
        .authorizeWithPopUp()
        .pipe(take(1))

      loginSubscription.subscribe((loginResponse) => {
        if (AuthService.SHOW_DEBUG) {
          console.log('[AuthService] Logged In: ', loginResponse)
        }
        if (loginResponse.isAuthenticated) {
          this.isAuthenticated.next(loginResponse.isAuthenticated)
          this.accessToken.next(loginResponse.accessToken)
          this.userProfile.next(this.getUserFromObject(loginResponse.userData))
          this.analyticsService.logLogin()
        }
      })
    } else {
      this.oidcSecurityService.authorize()
    }
  }

  logout(
    postLogoutRoute: AppRoute = AppRoute.intro,
    callback: (() => void) | undefined = undefined
  ) {
    this.oidcSecurityService.logoff().subscribe((result) => {
      try {
        this.oidcSecurityService.revokeAccessToken()
        this.oidcSecurityService.revokeRefreshToken()

        // reset session data
        this.accessToken.next(undefined)
        this.refreshToken.next(undefined)
        this.isAuthenticated.next(false)
        this.chatUserData.next(undefined)
        this.userProfile.next(undefined)

        // remove config from localStorage
        localStorage.removeItem(`0-sciper`)
      } catch {
        // nothing to do
      } finally {
        setTimeout(() => {
          this.navigationController.navigateRoot(`/${postLogoutRoute}`)
        }, 500)

        if (environment.oauthConfig.showDebugInformation) {
          if (AuthService.SHOW_DEBUG) {
            console.log(`[logout]`)
          }
        }

        if (callback) {
          callback()
        }
      }
    })
  }

  checkAuth(url?: string, configId?: string): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth(url, configId)
  }

  getAccessToken(): string {
    return this.accessToken.value
  }

  getRefreshToken(): string {
    return this.refreshToken.value
  }

  getAuthResult(): Observable<AuthResult> {
    const authResultObservable =
      this.oidcSecurityService.getAuthenticationResult()
    authResultObservable
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((authResult) => {
        if (authResult && !authResult.error) {
          this.refreshToken.next(authResult.refresh_token)
          this.accessToken.next(authResult.access_token)
        }
      })
    return authResultObservable
  }

  getTokens(): { accessToken: string; refreshToken: string } {
    const tokens: { accessToken: string; refreshToken: string } = {
      accessToken: undefined,
      refreshToken: undefined,
    }
    this.oidcSecurityService
      .getRefreshToken()
      .pipe(take(1))
      .subscribe((value) => {
        tokens.refreshToken = value
        this.refreshToken.next(value)
      })
    this.oidcSecurityService
      .getIdToken()
      .pipe(take(1))
      .subscribe((value) => {
        tokens.accessToken = value
        this.accessToken.next(value)
      })

    return tokens
  }

  refreshSession(): Observable<{ success: boolean; token?: string }> {
    return this.oidcSecurityService.forceRefreshSession().pipe(
      map((loginResponse) => {
        if (loginResponse.isAuthenticated) {
          this.accessToken.next(loginResponse.accessToken)
          if (this.userProfile.value === undefined) {
            this.getLoggedInUser().then((user) => {
              this.userProfile.next(user)
            })
          }
          if (!this.chatUserData.value) {
            this.refreshChatToken()
          }
        }

        return {
          success: loginResponse.isAuthenticated,
          token: loginResponse.accessToken,
        }
      })
    )
  }

  isLoggedIn(): boolean {
    return this.isAuthenticated.value
  }

  async getLoggedInUser(): Promise<UserProfile> {
    if (this.oidcSecurityService.getIdToken() == null) {
      return undefined
    }

    const token = await firstValueFrom(this.oidcSecurityService.getIdToken())
    const decodedToken = jwtDecode<UserOAuth2Model>(token)
    return this.getUserFromObject(decodedToken)
  }

  getUserProfile(): Observable<UserProfile> {
    return this.userProfile.asObservable()
  }

  private getUserFromObject(decodedToken: any): UserProfile {
    if (decodedToken === null || decodedToken === undefined) {
      return undefined
    }

    const user: UserProfile = new UserProfile()
    user.uuid = decodedToken.sub
    user.name = decodedToken.name
    user.username = decodedToken.preferred_username
    user.givenName = decodedToken.given_name
    user.familyName = decodedToken.family_name
    user.email = decodedToken.email

    return user
  }

  private checkUserRoles(token: string) {
    if (!token) {
      return
    }
    const jwtData = token.split('.')[1]
    const decodedJwtJsonData = window.atob(jwtData)
    const decodedJwtData = JSON.parse(decodedJwtJsonData)
    const roles = decodedJwtData.realm_access.roles as string[]
    const isAdmin =
      roles?.some((role) => role === ConstantsUtility.adminRole) ?? false
    const profile = this.userProfile.value ?? new UserProfile()
    profile.isAdmin = isAdmin
    this.userProfile.next(profile)
  }

  private refreshChatToken(
    callback: (token: string, userId: string) => void = undefined
  ) {
    if (this.isChatLoginActive) {
      return
    }
    this.isChatLoginActive = true
    this.getChatToken()
      .then((chatTokenResponse) => {
        const token = chatTokenResponse?.data?.authToken
        const userId = chatTokenResponse?.data?.userId
        if (
          chatTokenResponse.data?.me?.username !== this.userProfile.value.uuid
        ) {
          this.setChatUsername(token, userId).then(() =>
            this.setChatToken(token, userId)
          )
        } else {
          this.setChatToken(token, userId)
        }
        if (callback) {
          callback(token, userId)
        }
      })
      .finally(() => (this.isChatLoginActive = false))
  }

  private setChatToken(token: string, userId: string) {
    if (token && userId) {
      this.chatUserData.next({
        token,
        userId,
      })
    } else {
      this.chatUserData.next(undefined)
    }
  }

  // login to RocketChat
  private getChatToken(): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = environment.chatBasePath + '/login'
      const postData = {
        serviceName: 'keycloak',
        accessToken: this.getAccessToken(),
        appId: ConstantsUtility.authClientId,
        scope: ConstantsUtility.authScopes,
        expiresIn: 30,
      }

      this.httpClient
        .post<any>(url, postData)
        .pipe(take(1))
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error),
        })
    })
  }

  private setChatUsername(token: string, userId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = environment.chatBasePath + '/users.updateOwnBasicInfo'

      const postData = {
        data: {
          username: this.userProfile.value.uuid,
        },
      }

      this.httpClient
        .post<any>(url, postData, {
          headers: { 'X-Auth-Token': token, 'X-User-Id': userId },
        })
        .pipe(take(1))
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error),
        })
    })
  }
}
